<template>
  <view
    class="swiper-container"
    wx:ref="swipercontainer"
    wx:class="{{{ 'swiper-container-vertical': vertical }}}"
    bindtouchstart="handleTouchStart"
    bindtouchmove="handleTouchMove"
    bindtouchend="handleTouchEnd"
  >
    <view class="swiper-wrapper" wx:style="{{wrapperStyle}}">
      <SwipeItem
        wx:for="{{loopedSwiperList}}"
        wx:key="index"
        index="{{index}}"
        item="{{item}}"
        active="{{activeIndex === index}}"
        noTransition="{{noTransition}}"
        spaceBetween="{{spaceBetween}}"
        imageStyle="{{imageStyle}}"
        itemSize="{{itemSize}}"
        displayMultipleItems="{{displayMultipleItems}}"
        bindclick="handleClick"
      />
    </view>
  </view>
</template>

<script>
  import mpx, { createComponent } from '@mpxjs/core'

  createComponent({
    props: {
      indicatorDots: {
        type: Boolean,
        value: false
      },
      indicatorColor: {
        type: String,
        value: 'rgba(0, 0, 0, .3)'
      },
      indicatorActiveColor: {
        type: String,
        value: '#000000'
      },
      autoplay: {
        type: Boolean,
        value: false
      },
      current: {
        type: Number,
        value: 0
      },
      interval: {
        type: Number,
        value: 5000
      },
      duration: {
        type: Number,
        value: 500
      },
      circular: {
        type: Boolean,
        value: true
      },
      vertical: {
        type: Boolean,
        value: false
      },
      displayMultipleItems: {
        type: Number,
        value: 3
      },
      centeredSlides: {
        type: Boolean,
        value: true
      },
      spaceBetween: {
        type: Number,
        value: 0
      },
      swiperList: {
        type: Array,
        value: []
      },
      itemSize: {
        type: Number,
        optionalTypes: [String],
        value: 0
      },
      imageStyle: {
        type: Object,
        value: {}
      }
    },
    data() {
      return {
        swiperContainerWidth: 0,
        touchStartX: 0,
        touchEndX: 0,
        autoScrollTimer: null,
        activeIndex: 0,
        loopedSwiperList: [],
        noTransition: false,
        swiperLoad: false,
        screenWidth: 0
      }
    },
    created() {
      this.updateLoopedSwiperList()
      if (this.autoplay) {
        this.startAutoScroll()
      }
    },
    async ready() {
      const containerRect = await new Promise((resolve) => {
        this.$refs.swipercontainer
          .boundingClientRect((rect) => {
            resolve(rect)
          })
          .exec()
      })
      this.swiperContainerWidth = containerRect?.width || 0
      this.screenWidth = mpx.getSystemInfoSync()?.windowWidth || 0
      setTimeout(() => {
        this.swiperLoad = true
      }, this.duration)
    },
    watch: {
      current(val) {
        if (val !== this.activeIndex) {
          this.activeIndex = val
          this.updateLoopedSwiperList()
        }
      },
      realIndex(val, oldVal) {
        if (val !== oldVal) {
          this.triggerEvent('change', {
            current: val
          })
        }
      },
      swiperList: {
        handler(val) {
          this.updateLoopedSwiperList()
        },
        immediate: true,
        deep: true
      }
    },
    computed: {
      swiperContainerStyle() {
        return {
          flexDirection: this.vertical ? 'column' : 'row'
        }
      },
      wrapperStyle() {
        // 计算基本偏移量
        const itemSize = (this.itemSize / 750) * this.screenWidth
        let baseOffset = this.itemSize
          ? itemSize * this.activeIndex
          : (100 / this.displayMultipleItems) * this.activeIndex

        const offsetUnit = this.itemSize ? 'px' : '%' // 确定使用的单位
        let shift = 0,
          transformDirection = '-'

        if (this.centeredSlides) {
          shift = this.itemSize
            ? (this.swiperContainerWidth - itemSize) / 2
            : (100 - 100 / this.displayMultipleItems) / 2
          baseOffset -= shift // 居中幻灯片时调整偏移量
        }

        if (!this.circular) {
          // 非循环模式下的最大偏移量
          const maxOffset = this.itemSize
            ? itemSize * (this.swiperList.length - 1)
            : (100 / this.displayMultipleItems) * (this.swiperList.length - 1)

          if (this.activeIndex === 0) {
            transformDirection = '+'
            baseOffset = this.centeredSlides ? shift : 0 // 第一张幻灯片居中或靠左
          } else if (this.activeIndex === this.swiperList.length - 1) {
            baseOffset = this.centeredSlides ? maxOffset - shift : maxOffset // 最后一张幻灯片居中或靠右
          } else {
            baseOffset = Math.min(baseOffset, maxOffset - shift)
          }
        }

        // 根据布局方向确定移动的轴
        const direction = this.vertical ? 'Y' : 'X'

        return {
          transform: `translate3d(${transformDirection}${baseOffset}${offsetUnit}, 0, 0)`,
          transitionDuration: `${!this.swiperLoad || this.noTransition ? 0 : this.duration}ms`
        }
      },

      realIndex() {
        // 获取新的循环数组对应的真正索引
        if (this.circular) {
          const neededItems = this.displayMultipleItems
          let originalIndex = this.activeIndex - neededItems
          if (originalIndex < 0) {
            originalIndex = this.swiperList.length + originalIndex
          } else if (originalIndex >= this.swiperList.length) {
            originalIndex = originalIndex % this.swiperList.length
          }
          return originalIndex
        } else {
          return this.activeIndex
        }
      }
    },
    methods: {
      updateLoopedSwiperList() {
        if (this.circular) {
          const neededItems = this.displayMultipleItems
          const front = this.swiperList.slice(-neededItems) // 从末尾取出相应数量的元素
          const end = this.swiperList.slice(0, neededItems) // 从开始取出相应数量的元素
          this.loopedSwiperList = front.concat(this.swiperList, end) // 创建新的循环数组

          if (!this.loopedSwiperList.length) {
            this.activeIndex = 0
          } else {
            // 初始化activeIndex，保证从复制的第一组元素后的第一个原始slide开始
            this.activeIndex = neededItems
          }
        } else {
          this.loopedSwiperList = this.swiperList.slice()
          this.activeIndex = Math.min(Math.max(0, this.activeIndex), this.swiperList.length - 1)
        }
      },
      handleTouchStart(event) {
        this.touchStartX = event.touches[0].clientX
        this.stopAutoScroll()
      },
      handleTouchMove(event) {
        this.touchEndX = event.touches[0].clientX
      },
      handleTouchEnd(event) {
        this.touchEndX = event.changedTouches[0].clientX
        const direction = this.touchStartX - this.touchEndX
        if (Math.abs(direction) > 30) {
          // 简单的滑动响应阈值
          this.adjustIndex(direction > 0 ? 1 : -1)
        }
        if (this.autoplay) {
          this.startAutoScroll()
        }
      },
      adjustIndex(inc) {
        this.noTransition = false
        const newIndex = this.activeIndex + inc
        const neededItems = this.displayMultipleItems
        const maxIndex = neededItems + this.swiperList.length // 最大索引值是复制的开始索引加上原数组的长度
        if (this.circular) {
          this.activeIndex = newIndex
          if (newIndex >= maxIndex) {
            // 当新索引达到或超过最大索引时，重置到第一个原始数组元素的位置
            setTimeout(() => {
              this.noTransition = true
              this.$nextTick(() => {
                this.activeIndex = neededItems
              })
            }, this.duration + 50)
          } else if (newIndex < neededItems) {
            // 当新索引低于复制的开始索引时，跳转到原始数组的末尾对应的位置
            setTimeout(() => {
              this.noTransition = true
              this.$nextTick(() => {
                this.activeIndex = maxIndex - 1
              })
            }, this.duration + 50)
          }
        } else {
          this.activeIndex = Math.min(Math.max(0, newIndex), this.swiperList.length - 1)
        }
      },
      startAutoScroll() {
        this.autoScrollTimer = setInterval(() => {
          this.adjustIndex(1)
        }, this.interval)
      },
      stopAutoScroll() {
        if (this.autoScrollTimer) {
          clearInterval(this.autoScrollTimer)
          this.autoScrollTimer = null
        }
      },
      handleClick(data) {
        const { index, item } = data.detail
        this.triggerEvent('click', {
          index,
          item
        })
      }
    }
  })
</script>

<style scoped lang="stylus">
  .swiper-container
    width 100%
    height 100%
    overflow hidden
    position relative
    display flex
    justify-content flex-start
    align-items center
  .swiper-wrapper
    width 100%
    height 100%
    display flex
    transition transform 0.3s ease-in-out
  .swiper-container-vertical
    flex-direction column
</style>

<script name="json">
  module.exports = {
    component: true,
    usingComponents: {
      SwipeItem: './ts-web-swiper-item'
    }
  }
</script>